/**
 * Utility functions for managing tenant-specific styles
 * Now focused on dynamic content that Tailwind cannot handle
 */

import { errorAnalyticsPayload } from './analytics/errorAnalytics'
import { trackEvent } from './analytics/trackEvent'
import { DEFAULT_COLORS } from '@/constants/colors'
import { SYSTEM_FONT_STACK } from '@/constants/fonts'
import { escapeCSSString } from './cssEscape'
import { interpolateHexColorsOklab } from './color'

export interface TenantConfig {
  // Core colors tenants can still customize
  pageBackgroundColor?: string
  cardBackgroundColor?: string
  topNavBackgroundColor?: string
  borderColor?: string
  subtleAccentBackgroundColor?: string
  kitTimelineColor?: string
  defaultTextColor?: string
  primaryActionColor?: string
  primaryActionHoverColor?: string
  primaryActionPressedColor?: string
  descriptionTextColor?: string
  progressBarBackgroundColor?: string
  contrastAccentColor?: string
  hyperlinkColor?: string
  inverseTextColor?: string
  // Fonts (keep - Tailwind cannot handle dynamic font loading)
  fontFamily?: string
  fontFile?: { url: string }

  // Branding (keep - requires DOM manipulation)
  faviconLight?: { url: string }
  faviconDark?: { url: string }
}

// Universal Default FallBacks.
export const DEFAULT_TENANT_THEME: TenantConfig = {
  pageBackgroundColor: DEFAULT_COLORS['neutral-50'],
  cardBackgroundColor: DEFAULT_COLORS.white,
  topNavBackgroundColor: DEFAULT_COLORS.white,
  borderColor: DEFAULT_COLORS['neutral-200'],
  defaultTextColor: DEFAULT_COLORS['neutral-950'],
  primaryActionColor: DEFAULT_COLORS['blue-600'],
  descriptionTextColor: DEFAULT_COLORS['neutral-500'],
  contrastAccentColor: DEFAULT_COLORS['coral-500'],
  inverseTextColor: DEFAULT_COLORS.white,
}

export type ParsedCssColor = {
  red: number
  green: number
  blue: number
  alpha: number
}

function clamp01(value: number): number {
  if (value < 0) return 0
  if (value > 1) return 1
  return value
}

function clamp255(value: number): number {
  if (value < 0) return 0
  if (value > 255) return 255
  return value
}

function componentToHex(component: number): string {
  return Math.round(clamp255(component)).toString(16).padStart(2, '0').toUpperCase()
}

function rgbToHex(red: number, green: number, blue: number): string {
  return `#${componentToHex(red)}${componentToHex(green)}${componentToHex(blue)}`
}

function parseHexColor(value: string): ParsedCssColor | null {
  const raw = value.trim().replace(/^#/, '')
  if (![3, 6].includes(raw.length)) return null
  if (!/^[0-9a-fA-F]+$/.test(raw)) return null

  const normalized =
    raw.length === 3 ? `${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}` : raw.toUpperCase()

  const red = Number.parseInt(normalized.slice(0, 2), 16)
  const green = Number.parseInt(normalized.slice(2, 4), 16)
  const blue = Number.parseInt(normalized.slice(4, 6), 16)
  return { red, green, blue, alpha: 1 }
}

const RGB_RE = /^rgb\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)$/i
const RGBA_RE = /^rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:,\s*([\d.]+)\s*)?\)$/i

function parseRgbColor(value: string): ParsedCssColor | null {
  const trimmed = value.trim()
  const match = RGB_RE.exec(trimmed) ?? RGBA_RE.exec(trimmed)

  if (!match) return null

  const red = Number.parseFloat(match[1] || '')
  const green = Number.parseFloat(match[2] || '')
  const blue = Number.parseFloat(match[3] || '')
  const alpha = Number.parseFloat(match[4] || '1')

  if ([red, green, blue, alpha].some((component) => Number.isNaN(component))) return null

  return {
    red: clamp255(red),
    green: clamp255(green),
    blue: clamp255(blue),
    alpha: clamp01(alpha),
  }
}

export function parseCssColor(value: string | null | undefined): ParsedCssColor | null {
  if (!value) return null
  return parseRgbColor(value) ?? parseHexColor(value)
}

/**
 * Normalizes a CSS color string (hex or rgb/rgba) to uppercase 6-digit hex.
 * Returns null for unsupported or invalid values.
 */
export function normalizeToHexColor(value: string | null | undefined): string | null {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed.length) return null

  const parsedHex = parseHexColor(trimmed)
  if (parsedHex) {
    return rgbToHex(parsedHex.red, parsedHex.green, parsedHex.blue)
  }

  const parsedRgb = parseRgbColor(trimmed)
  if (parsedRgb) {
    return rgbToHex(parsedRgb.red, parsedRgb.green, parsedRgb.blue)
  }

  return null
}

/**
 * Normalizes a CSS color while preserving transparency for values that use it.
 * Opaque colors are returned as uppercase 6-digit hex values.
 */
export function normalizeToCssColor(value: string | null | undefined): string | null {
  const parsed = parseCssColor(value)
  if (!parsed) return null
  if (parsed.alpha === 1) {
    return rgbToHex(parsed.red, parsed.green, parsed.blue)
  }

  return `rgba(${Math.round(parsed.red)}, ${Math.round(parsed.green)}, ${Math.round(parsed.blue)}, ${Number(parsed.alpha.toFixed(4))})`
}

/**
 * Resolves a CSS color against an opaque background for contrast calculations.
 * CSS colors in the theme are limited to hex/rgb/rgba, so sRGB alpha compositing
 * is sufficient and keeps the checker aligned with the values rendered by CSS.
 */
export function resolveCssColorAgainstBackground(
  color: string | null | undefined,
  background: string | null | undefined = DEFAULT_COLORS.white,
): string | null {
  const foreground = parseCssColor(color)
  const parsedBackground = parseCssColor(background)
  if (!foreground || !parsedBackground) return null

  const resolvedBackground =
    parsedBackground.alpha === 1
      ? parsedBackground
      : parseCssColor(resolveCssColorAgainstBackground(background, DEFAULT_COLORS.white) ?? '')
  if (!resolvedBackground) return null

  const alpha = foreground.alpha
  return rgbToHex(
    foreground.red * alpha + resolvedBackground.red * (1 - alpha),
    foreground.green * alpha + resolvedBackground.green * (1 - alpha),
    foreground.blue * alpha + resolvedBackground.blue * (1 - alpha),
  )
}

/**
 * Mixes two CSS colors in OKLab, matching
 * `color-mix(in oklab, colorA calc(ratioA * 100%), colorB)`.
 * Derived theme tokens emit this as hex so contrast checks can parse the result.
 */
export function mixCssColors(colorA: string, colorB: string, ratioA: number): string {
  const normalizedA = normalizeToHexColor(colorA)
  const normalizedB = normalizeToHexColor(colorB)
  if (!normalizedA || !normalizedB) return colorA

  return (
    normalizeToHexColor(interpolateHexColorsOklab(normalizedA, normalizedB, 1 - ratioA)) || colorA
  )
}

export type TenantDerivedColors = {
  progressBarBackgroundColor: string
  subtleAccentBackgroundColor: string
  hyperlinkColor: string
  primaryActionHoverColor: string
  primaryActionPressedColor: string
  kitTimelineColor: string
}

/** Resolves unset derived tokens to OKLab-mixed hex, matching production theme CSS. */
export function getTenantDerivedColors(tenant: TenantConfig): TenantDerivedColors {
  const primaryActionColor =
    normalizeToCssColor(tenant.primaryActionColor) || DEFAULT_TENANT_THEME.primaryActionColor!
  const pageBackgroundColor =
    normalizeToCssColor(tenant.pageBackgroundColor) || DEFAULT_TENANT_THEME.pageBackgroundColor!
  const cardBackgroundColor =
    normalizeToCssColor(tenant.cardBackgroundColor) || DEFAULT_TENANT_THEME.cardBackgroundColor!

  const opaquePageBackgroundColor =
    resolveCssColorAgainstBackground(pageBackgroundColor, DEFAULT_COLORS.white) ??
    DEFAULT_COLORS.white
  const opaqueCardBackgroundColor =
    resolveCssColorAgainstBackground(cardBackgroundColor, opaquePageBackgroundColor) ??
    opaquePageBackgroundColor
  const opaquePrimaryActionOnPage =
    resolveCssColorAgainstBackground(primaryActionColor, opaquePageBackgroundColor) ??
    normalizeToHexColor(primaryActionColor) ??
    DEFAULT_COLORS['blue-600']
  const opaquePrimaryActionOnCard =
    resolveCssColorAgainstBackground(primaryActionColor, opaqueCardBackgroundColor) ??
    normalizeToHexColor(primaryActionColor) ??
    DEFAULT_COLORS['blue-600']
  const hyperlinkColor = normalizeToCssColor(tenant.hyperlinkColor) || primaryActionColor

  return {
    progressBarBackgroundColor:
      normalizeToCssColor(tenant.progressBarBackgroundColor) ||
      mixCssColors(opaquePrimaryActionOnPage, opaquePageBackgroundColor, 0.2),
    subtleAccentBackgroundColor:
      normalizeToCssColor(tenant.subtleAccentBackgroundColor) ||
      mixCssColors(opaquePrimaryActionOnCard, opaqueCardBackgroundColor, 0.03),
    hyperlinkColor,
    primaryActionHoverColor:
      normalizeToCssColor(tenant.primaryActionHoverColor) ||
      mixCssColors(opaquePrimaryActionOnCard, DEFAULT_COLORS.black, 0.7),
    primaryActionPressedColor:
      normalizeToCssColor(tenant.primaryActionPressedColor) ||
      mixCssColors(opaquePrimaryActionOnCard, DEFAULT_COLORS.black, 0.45),
    kitTimelineColor:
      normalizeToCssColor(tenant.kitTimelineColor) ||
      mixCssColors(opaquePrimaryActionOnCard, DEFAULT_COLORS.white, 0.3),
  }
}

/**
 * Returns true only for CSS color strings that can be safely persisted and
 * emitted into a CSS custom property value: 3- or 6-digit hex, rgb(), rgba().
 *
 * Values that do not match are rejected at the API route and Payload collection
 * level to prevent stored CSS injection via unbalanced ; or } characters.
 */
export function isValidDisplayColor(value: string | null | undefined): boolean {
  return normalizeToHexColor(value) !== null
}

/** WCAG "dark background" luminance threshold — backgrounds at or below this value need light (inverse) text. */
export const DARK_BACKGROUND_LUMINANCE_THRESHOLD = 0.179

/**
 * Computes WCAG relative luminance (0–1) for a CSS color string.
 * Supports 3- and 6-digit hex (#fff, #ffffff, no-# variants),
 * rgb(r, g, b), and rgba(r, g, b, a) — alpha is ignored since the effective
 * luminance depends on the compositing background, which is unknown here.
 * Returns 1 (white luminance) for unrecognised formats.
 */
export function colorLuminance(color: string): number {
  const rgb = parseRgbColor(color) ?? parseHexColor(color)
  if (!rgb) return 1

  const r = rgb.red / 255
  const g = rgb.green / 255
  const b = rgb.blue / 255

  const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

/**
 * Returns the appropriate foreground CSS variable for text placed on `bgColor`.
 * Uses the WCAG luminance threshold to decide between inverse (light) and default (dark) text.
 * For derived colors, callers should pass the resolved color and the surface underneath it so
 * transparent colors and state-specific tints use the foreground that matches what is rendered.
 */
export function chooseForegroundColorKey(
  bgColor: string,
  backgroundUnderlay?: string,
): 'inverseTextColor' | 'defaultTextColor' {
  const effectiveBackground =
    resolveCssColorAgainstBackground(bgColor, backgroundUnderlay) ?? bgColor
  return colorLuminance(effectiveBackground) <= DARK_BACKGROUND_LUMINANCE_THRESHOLD
    ? 'inverseTextColor'
    : 'defaultTextColor'
}

export function chooseForegroundColor(bgColor: string, backgroundUnderlay?: string): string {
  return chooseForegroundColorKey(bgColor, backgroundUnderlay) === 'inverseTextColor'
    ? `var(--tenant-inverse-text-color, ${DEFAULT_COLORS.white})`
    : `var(--tenant-default-text-color, ${DEFAULT_COLORS['neutral-950']})`
}

/**
 * Returns a record of CSS variables for the tenant
 */
export function getCssVariables(tenant: TenantConfig): Record<string, string> {
  const derivedColors = getTenantDerivedColors(tenant)
  const primaryActionColor =
    normalizeToCssColor(tenant.primaryActionColor) || DEFAULT_TENANT_THEME.primaryActionColor!
  const pageBackgroundColor =
    normalizeToCssColor(tenant.pageBackgroundColor) || DEFAULT_TENANT_THEME.pageBackgroundColor!
  const cardBackgroundColor =
    normalizeToCssColor(tenant.cardBackgroundColor) || DEFAULT_TENANT_THEME.cardBackgroundColor!
  const opaquePageBackgroundColor =
    resolveCssColorAgainstBackground(pageBackgroundColor, DEFAULT_COLORS.white) ??
    DEFAULT_COLORS.white
  const opaqueCardBackgroundColor =
    resolveCssColorAgainstBackground(cardBackgroundColor, opaquePageBackgroundColor) ??
    opaquePageBackgroundColor

  return {
    '--tenant-white': DEFAULT_COLORS.white,
    '--tenant-card-background-color': escapeCSSString(cardBackgroundColor),
    '--tenant-top-nav-background-color': escapeCSSString(
      tenant.topNavBackgroundColor || DEFAULT_TENANT_THEME.topNavBackgroundColor!,
    ),
    '--tenant-page-background-color': escapeCSSString(pageBackgroundColor),
    '--tenant-border-color': escapeCSSString(
      tenant.borderColor || DEFAULT_TENANT_THEME.borderColor!,
    ),
    '--tenant-subtle-accent-background-color': escapeCSSString(
      derivedColors.subtleAccentBackgroundColor,
    ),
    '--tenant-kit-timeline-color': escapeCSSString(derivedColors.kitTimelineColor),
    '--tenant-default-text-color': escapeCSSString(
      tenant.defaultTextColor || DEFAULT_TENANT_THEME.defaultTextColor!,
    ),
    '--tenant-primary-action-color': escapeCSSString(primaryActionColor),
    '--tenant-primary-action-hover-color': escapeCSSString(derivedColors.primaryActionHoverColor),
    '--tenant-primary-action-pressed-color': escapeCSSString(
      derivedColors.primaryActionPressedColor,
    ),
    '--tenant-description-text-color': escapeCSSString(
      tenant.descriptionTextColor || DEFAULT_TENANT_THEME.descriptionTextColor!,
    ),
    '--tenant-progress-bar-background-color': escapeCSSString(
      derivedColors.progressBarBackgroundColor,
    ),
    '--tenant-contrast-accent-color': escapeCSSString(
      tenant.contrastAccentColor || DEFAULT_TENANT_THEME.contrastAccentColor!,
    ),
    '--tenant-contrast-accent-mix-color': `color-mix(in oklab, var(--tenant-contrast-accent-color, ${DEFAULT_COLORS['coral-500']}) 20%, ${DEFAULT_COLORS.white})`,
    '--tenant-button-outline-active-color': `color-mix(in oklab, var(--tenant-primary-action-color, ${DEFAULT_COLORS['blue-600']}) 6%, var(--tenant-subtle-accent-background-color, ${DEFAULT_COLORS['blue-50']}))`,
    '--tenant-button-outline-pressed-color': `color-mix(in oklab, var(--tenant-primary-action-color, ${DEFAULT_COLORS['blue-600']}) 12%, var(--tenant-subtle-accent-background-color, ${DEFAULT_COLORS['blue-50']}))`,
    '--tenant-hyperlink-color': escapeCSSString(derivedColors.hyperlinkColor),
    '--tenant-inverse-text-color': escapeCSSString(
      tenant.inverseTextColor || DEFAULT_TENANT_THEME.inverseTextColor!,
    ),
    // Foreground colors — auto-computed from background luminance
    '--tenant-button-disabled-text-color': chooseForegroundColor(
      tenant.borderColor || DEFAULT_COLORS['neutral-200'],
    ),
    '--tenant-card-foreground-color': chooseForegroundColor(
      cardBackgroundColor,
      opaquePageBackgroundColor,
    ),
    '--tenant-top-nav-foreground-color': chooseForegroundColor(
      tenant.topNavBackgroundColor || DEFAULT_COLORS.white,
      opaquePageBackgroundColor,
    ),
    '--tenant-subtle-accent-foreground-color': chooseForegroundColor(
      derivedColors.subtleAccentBackgroundColor,
      opaqueCardBackgroundColor,
    ),
    '--tenant-primary-action-foreground-color': chooseForegroundColor(
      primaryActionColor,
      opaqueCardBackgroundColor,
    ),
    '--tenant-primary-action-hover-foreground-color': chooseForegroundColor(
      derivedColors.primaryActionHoverColor,
      opaqueCardBackgroundColor,
    ),
    '--tenant-primary-action-pressed-foreground-color': chooseForegroundColor(
      derivedColors.primaryActionPressedColor,
      opaqueCardBackgroundColor,
    ),
    '--tenant-input-text-color': chooseForegroundColor(DEFAULT_COLORS['white']),
    // Override the font family variable only when the tenant provides a custom
    // font. When absent, the :root definition in globals.css (AvantGarde) wins.
    ...(tenant.fontFamily
      ? { '--tenant-font-family': `'${escapeCSSString(tenant.fontFamily)}', ${SYSTEM_FONT_STACK}` }
      : {}),
  }
}

/**
 * Generates CSS variables for Tailwind to use
 * Only generates the variables that Tailwind classes reference
 */
export function generateCSSVariables(tenant: TenantConfig): string {
  let cssContent = ':root {\n'
  // Generate only the variables that Tailwind tenant classes use
  const variables = getCssVariables(tenant)

  Object.entries(variables).forEach(([property, value]) => {
    cssContent += `  ${property}: ${value};\n`
  })

  cssContent += '}\n\n'
  return cssContent
}

/**
 * Generates @font-face CSS declarations
 */
export function generateFontFaces(tenant: TenantConfig): string {
  let fontCSS = ''

  if (tenant.fontFamily && tenant.fontFile?.url) {
    const format = getFontFormat(tenant.fontFile.url)
    fontCSS += `@font-face {
  font-family: '${escapeCSSString(tenant.fontFamily)}';
  src: url('${escapeCSSString(tenant.fontFile.url)}') format('${format}');
  font-display: swap;
  font-style: normal;
}\n`
  }

  return fontCSS
}

/**
 * Determines font format from file extension
 */
export function getFontFormat(url: string): string {
  if (url.endsWith('.otf')) return 'opentype'
  if (url.endsWith('.ttf')) return 'truetype'
  if (url.endsWith('.woff')) return 'woff'
  if (url.endsWith('.woff2')) return 'woff2'
  return 'truetype' // default
}

const DEFAULT_FAVICON_ID = 'default-favicon-ico'
const TENANT_FAVICON_ID = 'tenant-favicon'
/** Legacy IDs from earlier dual-link favicon injection; cleaned up on apply/clear. */
const LEGACY_TENANT_FAVICON_IDS = ['tenant-favicon-ico', 'tenant-favicon-svg'] as const

/**
 * Infers a favicon MIME type from the file URL so browsers do not skip the icon
 * when it is mislabeled (e.g. a PNG declared as image/svg+xml).
 */
export function getFaviconMimeType(url: string): string {
  const pathname = (url.split('?')[0] ?? '').toLowerCase()
  if (pathname.endsWith('.svg')) return 'image/svg+xml'
  if (pathname.endsWith('.png')) return 'image/png'
  if (pathname.endsWith('.jpg') || pathname.endsWith('.jpeg')) return 'image/jpeg'
  if (pathname.endsWith('.webp')) return 'image/webp'
  if (pathname.endsWith('.gif')) return 'image/gif'
  if (pathname.endsWith('.ico')) return 'image/x-icon'
  // CMS recommends PNG/ICO; default to PNG for extensionless media URLs.
  return 'image/png'
}

function removeTenantFavicons(): void {
  document.getElementById(TENANT_FAVICON_ID)?.remove()
  LEGACY_TENANT_FAVICON_IDS.forEach((id) => {
    document.getElementById(id)?.remove()
  })
}

/**
 * Disables the React-owned default favicon without removing it from the DOM
 * (removing React-owned head nodes causes reconciliation errors).
 */
function setDefaultFaviconEnabled(enabled: boolean): void {
  const defaultFavicon = document.getElementById(DEFAULT_FAVICON_ID) as HTMLLinkElement | null
  if (!defaultFavicon) return

  if (enabled) {
    defaultFavicon.removeAttribute('media')
  } else {
    // Browsers ignore icon links whose media query does not match.
    defaultFavicon.media = 'not all'
  }
}

function upsertTenantFavicon(url: string): void {
  let favicon = document.getElementById(TENANT_FAVICON_ID) as HTMLLinkElement | null
  if (!favicon) {
    favicon = document.createElement('link')
    favicon.id = TENANT_FAVICON_ID
    document.head.appendChild(favicon)
  }

  favicon.rel = 'icon'
  favicon.type = getFaviconMimeType(url)
  favicon.href = url
}

/**
 * Applies tenant favicon to the document
 */
export function applyTenantFavicon(tenant: TenantConfig): void {
  // Prefer light favicon; fall back to dark when light is unset.
  const faviconUrl = tenant.faviconLight?.url ?? tenant.faviconDark?.url

  if (!faviconUrl) {
    removeTenantFavicons()
    setDefaultFaviconEnabled(true)
    return
  }

  setDefaultFaviconEnabled(false)
  // Drop legacy dual-type links so only the correctly typed tenant icon remains.
  LEGACY_TENANT_FAVICON_IDS.forEach((id) => {
    document.getElementById(id)?.remove()
  })
  upsertTenantFavicon(faviconUrl)
}

/**
 * Applies tenant fonts to the document body
 */
export function applyTenantFonts(tenant: TenantConfig): void {
  if (!tenant.fontFamily) {
    return
  }

  document.body.style.fontFamily = `'${escapeCSSString(tenant.fontFamily)}', ${SYSTEM_FONT_STACK}`
}

const TENANT_STYLE_OVERRIDE_ID = 'tenant-styles-overrides'

/**
 * Removes existing tenant styles from the document
 */
export function removeExistingTenantStyles(): void {
  const existingStyle = document.getElementById('tenant-styles')
  if (existingStyle) {
    existingStyle.remove()
  }
}

/**
 * Clears patient portal tenant theme injection from the document so client
 * portal routes can use their own styling after client-side navigation.
 */
export function clearTenantThemeStyles(): void {
  removeExistingTenantStyles()
  document.getElementById(TENANT_STYLE_OVERRIDE_ID)?.remove()
  document.body.style.fontFamily = ''
  removeTenantFavicons()
  setDefaultFaviconEnabled(true)
}

/**
 * Injects CSS content into the document head
 */
export function injectTenantStyles(cssContent: string): void {
  const styleElement = document.createElement('style')
  styleElement.id = 'tenant-styles'
  styleElement.textContent = cssContent
  document.head.appendChild(styleElement)
}

/**
 * Generates complete tenant CSS (variables + font-faces + body font)
 * This is a server-safe pure function that can be called during SSR
 */
export function generateTenantCSS(tenant: TenantConfig): string {
  const cssVariables = generateCSSVariables(tenant)
  const fontFaces = generateFontFaces(tenant)
  // Only emit an explicit html,body rule when a tenant override is present.
  // When absent the :root --tenant-font-family variable (AvantGarde) already
  // covers html,body via globals.css, so no extra rule is needed.
  const bodyFont = tenant.fontFamily
    ? `html,body{font-family:'${escapeCSSString(tenant.fontFamily)}',${SYSTEM_FONT_STACK};}\n`
    : ''
  return cssVariables + fontFaces + bodyFont
}

/**
 * Main function to apply all tenant styles
 * Now simplified to only handle dynamic content that Tailwind cannot manage
 */
export function applyTenantStyles(tenant: TenantConfig): void {
  try {
    // Remove existing styles
    removeExistingTenantStyles()

    // Generate CSS content (only variables and fonts)
    const fullCSS = generateTenantCSS(tenant)

    // Inject styles
    injectTenantStyles(fullCSS)

    // Apply favicon
    applyTenantFavicon(tenant)

    // Apply fonts to body
    applyTenantFonts(tenant)
  } catch (error) {
    trackEvent('tenant_styles:apply_fail', {
      ...errorAnalyticsPayload(error),
    })
  }
}
